import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { UpdateCompanyUserPersonalDetailsRequest, updateCompanyUserPersonalDetails } from 'core'
import getLoginUser from 'core/application/account/loginUser'
import { EmployeeForSaveDto, getEmployeeForSave } from 'core/application/companies/getEmployeeForSave'

import { phoneNumberRegex, zipNumberRegex } from 'common/components/utils/InputUtils'

import { Avatar, Button, Card, Category, Col, Icon, Notification, Row, SectionBlock, Skeleton } from 'common/components'
import { submitDateToServerFormat } from 'common/dateFormatters'
import { useCommandRequestV2, useLoginUserV2, useQueryRequestV2 } from 'common/hooks'

import EmployeePersonalDetailsForm from '../employeePersonalDetailsForm'

import './styles.less'

type EditEmployeePersonalDetailsFormProps = {
  submitHandler?: (values: any) => Promise<void>
  onSubmitted?: () => void
  disableWorkEmailInput?: boolean
  title?: string
  overrideGetEmployeeForSaveHandler?: (companyUserId?: string) => Promise<EmployeeForSaveDto>
}

const EditEmployeePersonalDetailsForm = (props: EditEmployeePersonalDetailsFormProps) => {
  const commandHandler = props.submitHandler ?? updateCompanyUserPersonalDetails
  const { t: translate } = useTranslation()
  const history = useHistory()
  const { companyUserId } = useParams<any>()

  const { user, setUser } = useLoginUserV2()

  const handler = props.overrideGetEmployeeForSaveHandler ? props.overrideGetEmployeeForSaveHandler : getEmployeeForSave
  const { data: employee, isLoading } = useQueryRequestV2<EmployeeForSaveDto>({
    initialState: null,
    queryFn: () => handler(companyUserId ?? user.companyUserId),
  })

  const { sendCommand: sendSaveCommand, isLoading: isCommandSaveLoading } = useCommandRequestV2(
    (request: UpdateCompanyUserPersonalDetailsRequest) => commandHandler(request),
  )
  const { isLoading: isRefreshUserLoading, sendQuery: sendRefreshUserRequest } = useQueryRequestV2({
    initialState: null,
  })

  const initialValues = {
    availableStates: employee?.availableStates ?? [],
    birthDay: employee?.birthDay ? submitDateToServerFormat(employee.birthDay) : null,
    firstName: employee?.firstName ?? '',
    lastName: employee?.lastName ?? '',
    personalEmail: employee?.personalEmail ?? '',
    receiveSms: employee?.receiveSms ?? false,
    stateOfResidenceId: employee?.stateOfResidenceId ?? '',
    workEmail: employee?.workEmail ?? '',
    workPhone: employee?.workPhone ?? '',
    identificationNumber: employee?.identificationNumber ?? '',
    zipCode: employee?.zipCode ?? '',
  }

  const submitForm = async (values: any) => {
    const request: UpdateCompanyUserPersonalDetailsRequest = {
      ...values,
      id: companyUserId ?? user.companyUserId,
      zipCode: values.zipCode ? values.zipCode.toString() : null,
    }

    const result = await sendSaveCommand(request)

    if (result.completed) {
      const latestUser = await sendRefreshUserRequest(() => getLoginUser())
      setUser(latestUser)

      if (props.onSubmitted) {
        props.onSubmitted()
      } else {
        Notification.open({
          message: translate('admin-dashboard.manage-people.edit-profile.employee-update-success'),
          type: 'success',
        })

        history.goBack()
      }
    }
  }

  return (
    <Row>
      <div className="p-16 bg-white flex align-center w-100">
        <Button
          type="default"
          size="small"
          className="mr-8"
          onClick={() => {
            history.goBack()
          }}
        >
          <Icon type="fa fa-arrow-left" fontAwesome />
        </Button>
        <p className="mb-0 font-16">
          &nbsp;
          {props.title ?? translate('admin-dashboard.manage-people.complete-profile.header-title')}
        </p>
      </div>

      <Col span={24}>
        <Card>
          <Skeleton loading={isLoading}>
            {employee && (
              <Formik
                enableReinitialize={true}
                validationSchema={formValidator(translate)}
                initialValues={initialValues}
                onSubmit={() => {}}
              >
                {(formik) => (
                  <form className="form-manage-people-wrapper pt-0" onSubmit={formik.handleSubmit}>
                    {initialValues && (
                      <>
                        <Row className="mb-8">
                          <Col md={12} lg={8} className="flex align-center lg-justify-end pr-16">
                            <Avatar
                              className="avatar-background"
                              shape="circle"
                              size="default"
                              children={formik.values.firstName[0] + formik.values.lastName[0]}
                            />
                            <p className="profile-name font-20 t-fullname">
                              {formik.values.firstName} {formik.values.lastName}
                            </p>
                          </Col>
                        </Row>

                        <SectionBlock
                          className="mb-24"
                          titleElement={
                            <>
                              <Icon className="far fa-user mr-8" />
                              <Category title={translate('admin-dashboard.manage-people.complete-profile.header-1')} />
                            </>
                          }
                        >
                          <EmployeePersonalDetailsForm disableWorkEmailInput={props.disableWorkEmailInput} />
                        </SectionBlock>

                        <Row className="pt-24">
                          <Col className="flex flex-end" span={24}>
                            <Button
                              className={'t-cancel-btn'}
                              onClick={() => {
                                history.goBack()
                              }}
                            >
                              {translate('general.cancel')}
                            </Button>

                            <Button
                              onClick={async () => {
                                await submitForm(formik.values)
                              }}
                              type="primary"
                              className="ml-8 t-submit-form-btn"
                              loading={isCommandSaveLoading || isRefreshUserLoading}
                              disabled={
                                !formValidator(translate).isValidSync(formik.values) ||
                                isCommandSaveLoading ||
                                isRefreshUserLoading
                              }
                            >
                              {translate('general.save')}
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}
                  </form>
                )}
              </Formik>
            )}
          </Skeleton>
        </Card>
      </Col>
    </Row>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    firstName: Yup.string().required(translate('general.required')),
    lastName: Yup.string().required(translate('general.required')),
    zipCode: Yup.string()
      .trim()
      .nullable()
      .matches(zipNumberRegex, translate('admin-signup.company-details.errors.zip-number-regex'))
      .required(translate('general.required')),

    workEmail: Yup.string()
      .nullable()
      .email(translate('admin-signup.signup.errors.email'))
      .required(translate('general.required')),
    personalEmail: Yup.string().nullable().email(translate('admin-signup.signup.errors.email')),
    birthDay: Yup.string()
      .nullable()
      .when('requireBirthDate', {
        is: (requireBirthDate: boolean) => requireBirthDate,
        then: Yup.string().required(translate('general.required')),
      }),
    workPhone: Yup.string()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .nullable()
      .matches(phoneNumberRegex, translate('admin-signup.company-details.errors.phone-number-regex'))
      .when('receiveSms', {
        is: (receiveSms: boolean) => receiveSms,
        then: Yup.string().required(' '),
      }),
    stateOfResidenceId: Yup.string().nullable().required(translate('general.required')),
    receiveSms: Yup.boolean().nullable().required(translate('general.required')),
  })

export default EditEmployeePersonalDetailsForm
